import type { PerformanceReportRow } from "@/types/reporting";
import { TEXT } from "@/constants/text";

interface VideoStage {
  label: string;
  value: number;
}

export interface FunnelStageShape extends VideoStage {
  // Percent of the Start stage, 0–100.
  percentOfStart: number;
  // Widths as a fraction (0–1) of the maximum funnel width.
  bottomWidth: number;
  topWidth: number;
}

// Spec v2: a stage's top width is `1 − 2.2 × (1 − stage ÷ start)` of the
// maximum width — steeper than proportional so small drop-offs stay visible.
const FUNNEL_TAPER = 2.2;
// Floor so a near-empty stage still renders as a visible sliver instead of
// going negative.
const FUNNEL_MIN_WIDTH = 0.1;

const STAGE_TEXT = TEXT.campaignOverview.videoQuartiles.stages;

export const extractVideoStages = (
  rows: PerformanceReportRow[]
): VideoStage[] => {
  const row = rows[0] ?? {};
  return [
    { label: STAGE_TEXT.start, value: row.video_start ?? 0 },
    { label: STAGE_TEXT.firstQuartile, value: row.video_first_quartile ?? 0 },
    { label: STAGE_TEXT.midpoint, value: row.video_midpoint ?? 0 },
    { label: STAGE_TEXT.thirdQuartile, value: row.video_third_quartile ?? 0 },
    { label: STAGE_TEXT.complete, value: row.video_complete ?? 0 },
  ];
};

// Each stage's bottom edge matches the next stage's top width; the final
// stage keeps its own width so it ends square.
export const buildFunnelShapes = (stages: VideoStage[]): FunnelStageShape[] => {
  const start = stages[0]?.value ?? 0;
  const topWidths = stages.map(({ value }) => {
    const ratio = start > 0 ? value / start : 0;
    return Math.max(FUNNEL_MIN_WIDTH, 1 - FUNNEL_TAPER * (1 - ratio));
  });

  return stages.map((stage, index) => ({
    ...stage,
    bottomWidth: topWidths[index + 1] ?? topWidths[index],
    percentOfStart: start > 0 ? (stage.value / start) * 100 : 0,
    topWidth: topWidths[index],
  }));
};

// video_start of 0 / null means a non-video campaign — an expected state, not
// an error. Drives the no-video page layout and the funnel's empty state.
export const hasVideoData = (rows: PerformanceReportRow[]): boolean =>
  (rows[0]?.video_start ?? 0) > 0;
